import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';
import { Users, Plus, Trash2, Edit2, Filter, Search, Info, X, CheckSquare, Square, Power, PowerOff, Download } from 'lucide-react';
import { segmentSchema } from '../lib/validation';
import { ZodError } from 'zod';
import { useDebounce } from '../hooks/useDebounce';
import { LoadingSkeleton } from '../components/LoadingSkeleton';
import { Pagination } from '../components/Pagination';
import { useBulkSelection } from '../hooks/useBulkSelection';
import { BulkActionsBar } from '../components/BulkActionsBar';
import { useFilterState } from '../hooks/useFilterState';
import { AdvancedFilters, ActiveFiltersDisplay } from '../components/AdvancedFilters';
import { exportToCSV, ExportColumn, formatDateForExport } from '../utils/exportData';

interface Segment {
  id: string;
  name: string;
  description: string;
  rules: SegmentRule[];
  company_id: string;
  is_active: boolean;
  member_count?: number;
  created_at: string;
  updated_at: string;
}

interface SegmentRule {
  field: string;
  operator: string;
  value: string | number;
}

export default function SegmentsPage() {
  const [segments, setSegments] = useState<Segment[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingSegment, setEditingSegment] = useState<Segment | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 300);
  const [showInstructions, setShowInstructions] = useState(true);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Advanced filters
  const [filtersOpen, setFiltersOpen] = useState(false);
  const { filters, setFilters, clearFilters, removeFilter, applyFilters } = useFilterState();

  const filterConfig = {
    dateRange: {
      label: 'Created Date Range',
      enabled: true,
    },
    multiSelect: {
      is_active: {
        label: 'Status',
        options: [
          { value: 'true', label: 'Active' },
          { value: 'false', label: 'Inactive' },
        ],
      },
      member_count_range: {
        label: 'Member Count',
        options: [
          { value: '0-10', label: '0-10 members' },
          { value: '11-50', label: '11-50 members' },
          { value: '51-100', label: '51-100 members' },
          { value: '101-500', label: '101-500 members' },
          { value: '501+', label: '501+ members' },
        ],
      },
    },
  };

  useEffect(() => {
    loadSegments();

    // Set up real-time subscription
    const channel = supabase
      .channel('segments-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'marketing_segments'
        },
        (payload) => {
          console.log('Segment change detected:', payload);
          // Reload segments when any change occurs
          loadSegments();
        }
      )
      .subscribe();

    // Cleanup subscription on unmount
    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const loadSegments = async () => {
    try {
      const { data, error } = await supabase
        .from('marketing_segments')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setSegments(data || []);
    } catch (error) {
      console.error('Error loading segments:', error);
      toast.error('Failed to load segments');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSegment = () => {
    setEditingSegment(null);
    setShowCreateModal(true);
  };

  const handleEditSegment = (segment: Segment) => {
    setEditingSegment(segment);
    setShowCreateModal(true);
  };

  const handleDeleteSegment = async (segmentId: string) => {
    if (!confirm('Are you sure you want to delete this segment?')) return;

    try {
      const { error } = await supabase
        .from('marketing_segments')
        .delete()
        .eq('id', segmentId);

      if (error) throw error;

      toast.success('Segment deleted successfully');
      loadSegments();
    } catch (error) {
      console.error('Error deleting segment:', error);
      toast.error('Failed to delete segment');
    }
  };

  const handleBulkDelete = async () => {
    if (!confirm(`Are you sure you want to delete ${bulkSelection.selectedCount} segment(s)?`)) return;

    try {
      const { error } = await supabase
        .from('marketing_segments')
        .delete()
        .in('id', bulkSelection.selectedIds);

      if (error) throw error;

      toast.success(`${bulkSelection.selectedCount} segment(s) deleted successfully`);
      bulkSelection.deselectAll();
      loadSegments();
    } catch (error) {
      console.error('Error deleting segments:', error);
      toast.error('Failed to delete segments');
    }
  };

  const handleBulkActivate = async () => {
    try {
      const { error } = await supabase
        .from('marketing_segments')
        .update({ is_active: true })
        .in('id', bulkSelection.selectedIds);

      if (error) throw error;

      toast.success(`${bulkSelection.selectedCount} segment(s) activated successfully`);
      bulkSelection.deselectAll();
      loadSegments();
    } catch (error) {
      console.error('Error activating segments:', error);
      toast.error('Failed to activate segments');
    }
  };

  const handleBulkDeactivate = async () => {
    try {
      const { error } = await supabase
        .from('marketing_segments')
        .update({ is_active: false })
        .in('id', bulkSelection.selectedIds);

      if (error) throw error;

      toast.success(`${bulkSelection.selectedCount} segment(s) deactivated successfully`);
      bulkSelection.deselectAll();
      loadSegments();
    } catch (error) {
      console.error('Error deactivating segments:', error);
      toast.error('Failed to deactivate segments');
    }
  };

  const handleToggleActive = async (segment: Segment) => {
    try {
      const { error } = await supabase
        .from('marketing_segments')
        .update({ is_active: !segment.is_active })
        .eq('id', segment.id);

      if (error) throw error;

      toast.success(`Segment ${!segment.is_active ? 'activated' : 'deactivated'}`);
      loadSegments();
    } catch (error) {
      console.error('Error updating segment:', error);
      toast.error('Failed to update segment');
    }
  };

  // Apply search filter
  let filteredSegments = segments.filter(segment =>
    segment.name.toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
    segment.description?.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
  );

  // Apply advanced filters
  filteredSegments = applyFilters(filteredSegments, 'created_at');

  // Apply member count range filter
  if (filters.multiSelect?.member_count_range && filters.multiSelect.member_count_range.length > 0) {
    filteredSegments = filteredSegments.filter((segment) => {
      const count = segment.member_count || 0;
      return filters.multiSelect!.member_count_range.some((range) => {
        if (range === '0-10') return count >= 0 && count <= 10;
        if (range === '11-50') return count >= 11 && count <= 50;
        if (range === '51-100') return count >= 51 && count <= 100;
        if (range === '101-500') return count >= 101 && count <= 500;
        if (range === '501+') return count >= 501;
        return false;
      });
    });
  }

  // Apply active status filter
  if (filters.multiSelect?.is_active && filters.multiSelect.is_active.length > 0) {
    filteredSegments = filteredSegments.filter((segment) => {
      return filters.multiSelect!.is_active.includes(String(segment.is_active));
    });
  }

  // Reset to page 1 when search term or filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, filters]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredSegments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedSegments = filteredSegments.slice(startIndex, endIndex);

  // Bulk selection (must be after paginatedSegments is defined)
  const bulkSelection = useBulkSelection(paginatedSegments, (segment) => segment.id);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };

  const handleExport = () => {
    try {
      const columns: ExportColumn<Segment>[] = [
        { key: 'name', header: 'Segment Name' },
        { key: 'description', header: 'Description' },
        { 
          key: 'is_active', 
          header: 'Status',
          format: (value) => (value ? 'Active' : 'Inactive')
        },
        { 
          key: 'member_count', 
          header: 'Member Count',
          format: (value) => String(Number(value) || 0)
        },
        { 
          key: 'rules', 
          header: 'Rules Count',
          format: (value) => (Array.isArray(value) ? String(value.length) : '0')
        },
        { 
          key: 'created_at', 
          header: 'Created Date',
          format: (value) => formatDateForExport(value as string | Date)
        },
      ];

      const filename = `segments-export-${new Date().toISOString().split('T')[0]}.csv`;
      exportToCSV(filteredSegments, columns, filename);
      toast.success(`Exported ${filteredSegments.length} segment(s) to CSV`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export segments');
    }
  };

  if (loading) {
    return (
      <div className="p-8">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Customer Segments</h1>
              <p className="text-gray-600 mt-2">Create and manage customer segments for targeted campaigns</p>
            </div>
            <button className="btn-primary flex items-center gap-2" disabled>
              <Plus size={20} />
              Create Segment
            </button>
          </div>
        </div>

        {/* Search Bar Skeleton */}
        <div className="mb-6">
          <div className="animate-pulse">
            <div className="h-10 bg-gray-200 rounded w-full max-w-md"></div>
          </div>
        </div>

        {/* Stats Cards Skeleton */}
        <LoadingSkeleton type="stats" count={3} />

        {/* Segments List Skeleton */}
        <LoadingSkeleton type="list" count={3} />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Customer Segments</h1>
            <p className="text-gray-600 mt-2">Create and manage customer segments for targeted campaigns</p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={handleExport}
              className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 flex items-center gap-2 transition-colors"
              disabled={filteredSegments.length === 0}
            >
              <Download size={18} />
              Export CSV
            </button>
            <button
              onClick={handleCreateSegment}
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={20} />
              Create Segment
            </button>
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search segments..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input pl-10 w-full max-w-md"
          />
        </div>
      </div>

      {/* Advanced Filters */}
      <AdvancedFilters
        isOpen={filtersOpen}
        onToggle={() => setFiltersOpen(!filtersOpen)}
        filters={filters}
        onFiltersChange={setFilters}
        onClearFilters={clearFilters}
        filterConfig={filterConfig}
      />

      {/* Active Filters Display */}
      <ActiveFiltersDisplay
        filters={filters}
        filterConfig={filterConfig}
        onRemoveFilter={removeFilter}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Segments</p>
              <p className="text-3xl font-bold text-gray-900 mt-1">{segments.length}</p>
            </div>
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
              <Filter className="text-blue-600" size={24} />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Active Segments</p>
              <p className="text-3xl font-bold text-green-600 mt-1">
                {segments.filter(s => s.is_active).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <Users className="text-green-600" size={24} />
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Inactive Segments</p>
              <p className="text-3xl font-bold text-gray-400 mt-1">
                {segments.filter(s => !s.is_active).length}
              </p>
            </div>
            <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
              <Filter className="text-gray-400" size={24} />
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={bulkSelection.selectedCount}
        onDeselectAll={bulkSelection.deselectAll}
        onBulkDelete={handleBulkDelete}
        additionalActions={[
          {
            label: 'Activate',
            icon: <Power size={18} />,
            onClick: handleBulkActivate,
            variant: 'secondary',
          },
          {
            label: 'Deactivate',
            icon: <PowerOff size={18} />,
            onClick: handleBulkDeactivate,
            variant: 'secondary',
          },
        ]}
      />

      {/* Segments List */}
      {paginatedSegments.length === 0 && filteredSegments.length === 0 ? (
        <div className="card text-center py-12">
          <Filter className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            {searchTerm ? 'No segments found' : 'No segments yet'}
          </h3>
          <p className="text-gray-600 mb-4">
            {searchTerm
              ? 'Try adjusting your search terms'
              : 'Create your first segment to start targeting specific customer groups'}
          </p>
          {!searchTerm && (
            <button onClick={handleCreateSegment} className="btn-primary">
              <Plus size={20} className="inline mr-2" />
              Create Your First Segment
            </button>
          )}
        </div>
      ) : paginatedSegments.length === 0 ? (
        <div className="card text-center py-12">
          <Filter className="mx-auto text-gray-400 mb-4" size={48} />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            No segments on this page
          </h3>
          <p className="text-gray-600 mb-4">
            Try going to a different page or adjusting your filters
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Select All Header */}
          {paginatedSegments.length > 0 && (
            <div className="card bg-gray-50">
              <div className="flex items-center gap-3">
                <button
                  onClick={bulkSelection.toggleSelectAll}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                  title={bulkSelection.allSelected ? 'Deselect all' : 'Select all'}
                >
                  {bulkSelection.allSelected ? (
                    <CheckSquare className="text-blue-600" size={20} />
                  ) : bulkSelection.someSelected ? (
                    <CheckSquare className="text-blue-400" size={20} />
                  ) : (
                    <Square className="text-gray-400" size={20} />
                  )}
                </button>
                <span className="text-sm text-gray-600 font-medium">
                  {bulkSelection.selectedCount > 0
                    ? `${bulkSelection.selectedCount} selected`
                    : 'Select all'}
                </span>
              </div>
            </div>
          )}

          {paginatedSegments.map((segment) => (
            <div key={segment.id} className="card hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1">
                  {/* Checkbox */}
                  <button
                    onClick={() => bulkSelection.toggleSelect(segment.id)}
                    className="p-1 hover:bg-gray-100 rounded transition-colors mt-1"
                    title={bulkSelection.isSelected(segment.id) ? 'Deselect' : 'Select'}
                  >
                    {bulkSelection.isSelected(segment.id) ? (
                      <CheckSquare className="text-blue-600" size={20} />
                    ) : (
                      <Square className="text-gray-400" size={20} />
                    )}
                  </button>

                  <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-gray-900">{segment.name}</h3>
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        segment.is_active
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {segment.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {segment.description && (
                    <p className="text-gray-600 mb-3">{segment.description}</p>
                  )}
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Filter size={16} />
                      {segment.rules?.length || 0} rule(s)
                    </span>
                    <span className="flex items-center gap-1">
                      <Users size={16} />
                      {segment.member_count || 0} members
                    </span>
                    <span>
                      Created {new Date(segment.created_at).toLocaleDateString()}
                    </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => handleToggleActive(segment)}
                    className={`px-3 py-1 rounded text-sm font-medium transition-colors ${
                      segment.is_active
                        ? 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    {segment.is_active ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleEditSegment(segment)}
                    className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                    title="Edit segment"
                  >
                    <Edit2 size={18} />
                  </button>
                  <button
                    onClick={() => handleDeleteSegment(segment.id)}
                    className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                    title="Delete segment"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Pagination */}
      {filteredSegments.length > 0 && (
        <div className="mt-6 card">
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            itemsPerPage={itemsPerPage}
            totalItems={filteredSegments.length}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      )}

      {/* Instructions Panel */}
      {showInstructions && (
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-6 mt-8">
          <div className="flex items-start justify-between mb-4">
            <div className="flex items-center gap-2">
              <Info className="w-5 h-5 text-purple-600 flex-shrink-0" />
              <h3 className="text-lg font-semibold text-purple-900">How to Use Segments</h3>
            </div>
            <button
              onClick={() => setShowInstructions(false)}
              className="text-purple-600 hover:text-purple-700"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="space-y-4 text-sm text-purple-900">
            <div>
              <h4 className="font-semibold mb-2">🎯 Creating Segments</h4>
              <p className="text-purple-800">
                Click "Create Segment" to define customer groups based on specific criteria. Segments help you target campaigns to the right audience, improving engagement and conversion rates.
              </p>
            </div>

            <div>
              <h4 className="font-semibold mb-2">📊 Segment Criteria</h4>
              <div className="space-y-2 text-purple-800">
                <div><strong>Days Since Last Service:</strong> Target customers based on service recency (e.g., overdue for maintenance)</div>
                <div><strong>Total Spend:</strong> Segment by customer lifetime value</div>
                <div><strong>Service Count:</strong> Identify frequent vs. infrequent customers</div>
                <div><strong>Agreement Status:</strong> Filter by active/expired service agreements</div>
                <div><strong>Zip Code:</strong> Target specific geographic areas</div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">🔧 Building Rules</h4>
              <ul className="list-disc list-inside space-y-1 text-purple-800">
                <li><strong>Multiple Rules:</strong> Add multiple criteria - customers must match ALL rules to be included</li>
                <li><strong>Operators:</strong> Use equals, greater than, less than, contains to refine targeting</li>
                <li><strong>Dynamic Updates:</strong> Segments automatically update as customer data changes</li>
                <li><strong>Member Count:</strong> View real-time count of customers matching your criteria</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">💡 Segment Strategies</h4>
              <div className="grid grid-cols-2 gap-2 text-purple-800 text-xs">
                <div><strong>Win-Back:</strong> Last service &gt; 180 days</div>
                <div><strong>VIP Customers:</strong> Total spend &gt; $5,000</div>
                <div><strong>New Customers:</strong> Service count = 1</div>
                <div><strong>Agreement Expired:</strong> Status = expired</div>
                <div><strong>High Risk Churn:</strong> Last service &gt; 365 days</div>
                <div><strong>Geographic Target:</strong> Specific zip codes</div>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-2">⚡ Using Segments</h4>
              <ul className="list-disc list-inside space-y-1 text-purple-800">
                <li><strong>Campaign Targeting:</strong> Select segments when creating campaigns</li>
                <li><strong>Active/Inactive:</strong> Toggle segment status to enable/disable for campaigns</li>
                <li><strong>Bulk Actions:</strong> Activate, deactivate, or delete multiple segments at once</li>
                <li><strong>Export:</strong> Download segment data for analysis in external tools</li>
              </ul>
            </div>

            <div className="bg-purple-100 rounded p-3 mt-4">
              <p className="text-purple-900">
                <strong>💡 Pro Tip:</strong> Create focused segments with 2-3 rules for better targeting. Use the member count to validate your criteria before launching campaigns. Test with a small segment first.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <SegmentModal
          segment={editingSegment}
          onClose={() => {
            setShowCreateModal(false);
            setEditingSegment(null);
          }}
          onSave={() => {
            setShowCreateModal(false);
            setEditingSegment(null);
            loadSegments();
          }}
        />
      )}
    </div>
  );
}

// ============================================================================
// SEGMENT MODAL COMPONENT
// ============================================================================

function SegmentModal({
  segment,
  onClose,
  onSave,
}: {
  segment: Segment | null;
  onClose: () => void;
  onSave: () => void;
}) {
  const [name, setName] = useState(segment?.name || '');
  const [description, setDescription] = useState(segment?.description || '');
  const [isActive, setIsActive] = useState(segment?.is_active ?? true);
  const [rules, setRules] = useState<SegmentRule[]>(segment?.rules || [
    { field: 'last_service_days', operator: 'greater_than', value: 90 }
  ]);
  const [saving, setSaving] = useState(false);

  const ruleFields = [
    { value: 'last_service_days', label: 'Days Since Last Service' },
    { value: 'total_spend', label: 'Total Spend' },
    { value: 'service_count', label: 'Service Count' },
    { value: 'agreement_status', label: 'Agreement Status' },
    { value: 'zip_code', label: 'Zip Code' },
  ];

  const operators = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'greater_than', label: 'Greater Than' },
    { value: 'less_than', label: 'Less Than' },
    { value: 'contains', label: 'Contains' },
  ];

  const addRule = () => {
    setRules([...rules, { field: 'last_service_days', operator: 'greater_than', value: '' }]);
  };

  const removeRule = (index: number) => {
    setRules(rules.filter((_, i) => i !== index));
  };

  const updateRule = (index: number, field: keyof SegmentRule, value: any) => {
    const newRules = [...rules];
    newRules[index] = { ...newRules[index], [field]: value };
    setRules(newRules);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Prepare form data for validation
      const formData = {
        name: name.trim(),
        description: description.trim() || undefined,
        rules: rules.map(rule => ({
          field: rule.field,
          operator: rule.operator,
          value: String(rule.value).trim()
        }))
      };

      // Validate using Zod schema
      const validatedData = segmentSchema.parse(formData);

      const segmentData = {
        ...validatedData,
        is_active: isActive,
        company_id: 'halesac', // Default company - TODO: Get from auth context
      };

      if (segment) {
        // Update existing segment
        const { error } = await supabase
          .from('marketing_segments')
          .update(segmentData)
          .eq('id', segment.id);

        if (error) throw error;
        toast.success('Segment updated successfully');
      } else {
        // Create new segment
        const { error } = await supabase
          .from('marketing_segments')
          .insert(segmentData);

        if (error) throw error;
        toast.success('Segment created successfully');
      }

      onSave();
    } catch (error) {
      if (error instanceof ZodError) {
        // Display validation errors
        const firstError = error.issues[0];
        toast.error(firstError.message);
      } else {
        console.error('Error saving segment:', error);
        toast.error('Failed to save segment');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">
            {segment ? 'Edit Segment' : 'Create New Segment'}
          </h2>

          {/* Basic Info */}
          <div className="space-y-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Segment Name *
              </label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g., High-Value Customers"
                className="input w-full"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Describe this segment..."
                rows={2}
                className="input w-full"
              />
            </div>

            <div>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700">
                  Active (segment will be available for campaigns)
                </span>
              </label>
            </div>
          </div>

          {/* Rules */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-gray-700">
                Segmentation Rules *
              </label>
              <button
                onClick={addRule}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                + Add Rule
              </button>
            </div>

            <div className="space-y-3">
              {rules.map((rule, index) => (
                <div key={index} className="flex items-center gap-2 p-3 bg-gray-50 rounded">
                  <select
                    value={rule.field}
                    onChange={(e) => updateRule(index, 'field', e.target.value)}
                    className="input flex-1"
                  >
                    {ruleFields.map((field) => (
                      <option key={field.value} value={field.value}>
                        {field.label}
                      </option>
                    ))}
                  </select>

                  <select
                    value={rule.operator}
                    onChange={(e) => updateRule(index, 'operator', e.target.value)}
                    className="input flex-1"
                  >
                    {operators.map((op) => (
                      <option key={op.value} value={op.value}>
                        {op.label}
                      </option>
                    ))}
                  </select>

                  <input
                    type="text"
                    value={rule.value}
                    onChange={(e) => updateRule(index, 'value', e.target.value)}
                    placeholder="Value"
                    className="input flex-1"
                  />

                  <button
                    onClick={() => removeRule(index)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded"
                    title="Remove rule"
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              ))}
            </div>

            <p className="text-xs text-gray-500 mt-2">
              Customers must match ALL rules to be included in this segment
            </p>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-4 border-t">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded transition-colors"
              disabled={saving}
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="btn-primary"
            >
              {saving ? 'Saving...' : segment ? 'Update Segment' : 'Create Segment'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
